<script lang="ts" setup>
import { computed } from 'vue';

import { DestinationSelector } from '@apps/main/new-desktop/modules/destinationSelector';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';

import type { AutoCompleteSuggestion } from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import type {
  EditTripSelectDestinationStep,
  SelectDestinationEmit,
} from '../../models/editCarTripView.model';
import {
  EditTripStepType,
  SelectDestinationEmitType,
} from '../../models/editCarTripView.model';

interface SelectDestinationStepProps {
  step: EditTripSelectDestinationStep;
  autocompleteType:
    | AutoCompleteType.Destination
    | AutoCompleteType.StartingPosition
    | AutoCompleteType.EndingPosition;
}
const props = defineProps<SelectDestinationStepProps>();

interface SelectDestinationStepEmits {
  (eventName: 'select-destination', payload: SelectDestinationEmit): void;
  (
    eventName: 'select-my-position',
    autocompleteType: DestinationAutocompleteType
  ): void;
}
const emit = defineEmits<SelectDestinationStepEmits>();

const initialSearchText = computed<string>(() => {
  if (
    props.step.stepType === EditTripStepType.UpdateDestination &&
    props.autocompleteType === AutoCompleteType.Destination
  ) {
    return props.step.currentEditingSegment.destinationName;
  }

  return '';
});

function selectDestination(suggestion: AutoCompleteSuggestion): void {
  switch (props.step.stepType) {
    case EditTripStepType.AddDestinationToOwnStay:
      emit('select-destination', {
        type: SelectDestinationEmitType.AddDestinationToOwnStay,
        suggestion,
        ingredientId: props.step.ownStayDestinationIngredeintId,
      });
      break;

    case EditTripStepType.UpdateDestination:
      emit('select-destination', {
        type: SelectDestinationEmitType.UpdateDestination,
        suggestion,
        ingredientId: props.step.currentEditingSegment.ingredientId,
      });
      break;

    case EditTripStepType.AddDestination:
      emit('select-destination', {
        type: SelectDestinationEmitType.AddDestination,
        suggestion,
      });
      break;

    default:
      exhaustiveGuard(props.step, 'Invalid select destination step');
  }
}

function selectMyPosition(autocompleteType: DestinationAutocompleteType): void {
  emit('select-my-position', autocompleteType);
}
</script>

<template>
  <DestinationSelector
    :search-text="initialSearchText"
    :autocomplete-type="props.autocompleteType"
    @select-destination="selectDestination"
    @select-my-position="selectMyPosition"
  />
</template>
